import React, { useState, useEffect } from 'react';
import {
  KeyIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  XMarkIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  BuildingOffice2Icon,
  GlobeAltIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';
import {
  obtenerEstadoAsistenteAfip,
  generarSolicitudAfip,
  subirCertificadoAfip,
  probarConexionAfip,
} from '../api/backend_v2';

const CONDICIONES_IVA = [
  { id: 'IVA Responsable Inscripto', nombre: 'IVA Responsable Inscripto' },
  { id: 'Responsable Monotributo', nombre: 'Responsable Monotributo' },
  { id: 'IVA Sujeto Exento', nombre: 'IVA Sujeto Exento' },
  { id: 'Consumidor Final', nombre: 'Consumidor Final' },
];

export default function ModalAsistenteAfip({ isOpen, onClose, onConfigGuardada }) {
  const [paso, setPaso] = useState(1);
  const [loading, setLoading] = useState(false);
  const [estadoActual, setEstadoActual] = useState(null);

  // Formulario Paso 1
  const [form, setForm] = useState({
    cuit: '',
    punto_venta: 1,
    razon_social: '',
    condicion_iva: 'IVA Responsable Inscripto',
    domicilio_fiscal: '',
    numero_iibb: '',
    inicio_actividades: '',
    afip_env: 'production',
  });

  // Datos de CSR generado (Paso 2)
  const [csrData, setCsrData] = useState(null);

  // Archivo CRT seleccionado (Paso 4)
  const [archivoCrt, setArchivoCrt] = useState(null);
  const [resultadoVinculacion, setResultadoVinculacion] = useState(null);
  const [resultadoTest, setResultadoTest] = useState(null);
  const [testeando, setTesteando] = useState(false);

  useEffect(() => {
    if (isOpen) {
      cargarEstado();
    }
  }, [isOpen]);

  const cargarEstado = async () => {
    try {
      setLoading(true);
      const res = await obtenerEstadoAsistenteAfip();
      setEstadoActual(res);

      if (res) {
        setForm({
          cuit: res.cuit || '',
          punto_venta: res.punto_venta || 1,
          razon_social: res.razon_social || '',
          condicion_iva: res.condicion_iva || 'IVA Responsable Inscripto',
          domicilio_fiscal: res.domicilio_fiscal || '',
          numero_iibb: res.numero_iibb || '',
          inicio_actividades: res.inicio_actividades || '',
          afip_env: res.afip_env === 'TESTING' ? 'testing' : 'production',
        });

        // Si ya tiene clave y certificado activos, arranca en el paso de estado/test
        if (res.configurado) {
          setPaso(4);
          setResultadoVinculacion({
            cuit: res.cuit,
            punto_venta: res.punto_venta,
            valido_hasta: res.valido_hasta,
            ambiente: res.afip_env,
          });
        } else if (res.tiene_clave && !res.tiene_cert) {
          // Ya generó clave/CSR pero le falta subir el certificado
          setPaso(3);
        } else {
          setPaso(1);
        }
      }
    } catch (err) {
      console.error('Error al cargar estado de AFIP:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Paso 1 -> 2: Validar datos
  const handleAvanzarPaso1 = (e) => {
    e.preventDefault();
    const cuitLimpio = String(form.cuit).replace(/\D/g, '');
    if (cuitLimpio.length !== 11) {
      Swal.fire({
        icon: 'warning',
        title: 'CUIT inválido',
        text: 'El CUIT debe contener exactamente 11 números (sin guiones).',
      });
      return;
    }
    if (!form.punto_venta || Number(form.punto_venta) <= 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Punto de Venta requerido',
        text: 'Ingresá un número de punto de venta habilitado en AFIP mayor a 0.',
      });
      return;
    }
    setPaso(2);
  };

  // Paso 2: Generar y descargar CSR
  const handleGenerarCsr = async () => {
    try {
      setLoading(true);
      const res = await generarSolicitudAfip(form);
      setCsrData(res);

      // Disparar descarga automática del archivo .csr
      descargarArchivoTexto(res.csr, res.filename || `pedido_afip_${form.cuit}.csr`);

      Swal.fire({
        icon: 'success',
        title: '¡Solicitud generada!',
        text: 'El archivo de pedido (CSR) se descargó a tu computadora y tu clave privada quedó guardada y cifrada de forma segura.',
        confirmButtonText: 'Continuar a las instrucciones',
      }).then(() => {
        setPaso(3);
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error al generar solicitud',
        text: err.message || 'No se pudo generar la clave y CSR.',
      });
    } finally {
      setLoading(false);
    }
  };

  const descargarArchivoTexto = (contenido, nombreArchivo) => {
    const blob = new Blob([contenido], { type: 'application/x-pem-file;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nombreArchivo;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Paso 4: Subir archivo CRT
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setArchivoCrt(e.target.files[0]);
    }
  };

  const handleSubirCertificado = async () => {
    if (!archivoCrt) {
      Swal.fire({
        icon: 'warning',
        title: 'Seleccioná un archivo',
        text: 'Por favor seleccioná el archivo .crt emitido por AFIP.',
      });
      return;
    }

    try {
      setLoading(true);
      const res = await subirCertificadoAfip(archivoCrt);
      setResultadoVinculacion(res);

      await Swal.fire({
        icon: 'success',
        title: '¡Certificado Activado!',
        text: `El certificado fue validado y cifrado con éxito. Válido hasta el ${res.valido_hasta || 'N/D'}.`,
        confirmButtonText: 'Probar conexión ahora',
      });

      if (onConfigGuardada) {
        onConfigGuardada();
      }

      // Ejecutar prueba de conexión inmediatamente
      handleTestConexion();
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error al vincular certificado',
        text: err.message || 'No se pudo validar el certificado.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestConexion = async () => {
    try {
      setTesteando(true);
      setResultadoTest(null);
      const res = await probarConexionAfip();
      setResultadoTest(res);
      if (res.ok) {
        if (onConfigGuardada) onConfigGuardada();
      }
    } catch (err) {
      setResultadoTest({
        ok: false,
        mensaje: err.message || 'Error de comunicación con AFIP',
      });
    } finally {
      setTesteando(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1050] flex items-center justify-center p-3 sm:p-4 overflow-y-auto bg-slate-950/80 backdrop-blur-sm overscroll-contain animate-fadeIn"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col my-auto max-h-[90vh] sm:max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 bg-slate-50 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-indigo-50 border border-indigo-100 text-indigo-600">
              <ShieldCheckIcon className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">
                Asistente de Certificados y Facturación AFIP
              </h2>
              <p className="text-xs text-slate-500">
                Configurá tu CUIT y vinculá tu firma digital para emitir comprobantes electrónicos
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200/50 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Stepper */}
        <div className="px-6 py-3 bg-white border-b border-slate-100 flex items-center justify-between">
          {[
            { n: 1, label: 'Datos Fiscales' },
            { n: 2, label: 'Generar Solicitud' },
            { n: 3, label: 'Trámite en AFIP' },
            { n: 4, label: 'Certificado y Test' },
          ].map((s) => {
            const activo = paso === s.n;
            const completado = paso > s.n;
            return (
              <button
                key={s.n}
                onClick={() => {
                  // Permitir navegar hacia atrás o a pasos accesibles
                  if (s.n <= paso || estadoActual?.tiene_clave) {
                    setPaso(s.n);
                  }
                }}
                className={`flex items-center gap-2 text-xs font-semibold py-1 px-2.5 rounded-lg transition-colors ${
                  activo
                    ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                    : completado
                    ? 'text-emerald-700 hover:bg-emerald-50'
                    : 'text-slate-400 cursor-not-allowed'
                }`}
              >
                <span
                  className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${
                    activo
                      ? 'bg-indigo-600 text-white'
                      : completado
                      ? 'bg-emerald-600 text-white'
                      : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {completado ? '✓' : s.n}
                </span>
                <span className="hidden sm:inline">{s.label}</span>
              </button>
            );
          })}
        </div>

        {/* Body con scroll */}
        <div className="p-6 overflow-y-auto flex-1 space-y-5">
          {loading && (
            <div className="py-12 flex flex-col items-center justify-center gap-3 text-slate-500">
              <ArrowPathIcon className="w-8 h-8 animate-spin text-indigo-600" />
              <p className="text-sm font-medium">Procesando solicitud segura...</p>
            </div>
          )}

          {!loading && (
            <>
              {/* PASO 1: DATOS FISCALES */}
              {paso === 1 && (
                <form onSubmit={handleAvanzarPaso1} className="space-y-4">
                  <div className="p-3.5 bg-blue-50/70 border border-blue-200 rounded-xl flex items-start gap-3">
                    <InformationCircleIcon className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-800 leading-relaxed">
                      Ingresá los datos fiscales de la óptica exactamente como figuran en tu constancia de inscripción de AFIP / ARCA.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        CUIT del Emisor (11 dígitos sin guiones) *
                      </label>
                      <input
                        type="text"
                        name="cuit"
                        required
                        maxLength={11}
                        placeholder="Ej: 27110985709"
                        value={form.cuit}
                        onChange={handleInputChange}
                        className="w-full text-sm px-3.5 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-mono"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Punto de Venta Habilitado (Web Services) *
                      </label>
                      <input
                        type="number"
                        name="punto_venta"
                        required
                        min={1}
                        placeholder="Ej: 2 o 3"
                        value={form.punto_venta}
                        onChange={handleInputChange}
                        className="w-full text-sm px-3.5 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Razón Social / Nombre y Apellido
                      </label>
                      <input
                        type="text"
                        name="razon_social"
                        placeholder="Ej: Óptica Visión S.R.L. o Tu Nombre"
                        value={form.razon_social}
                        onChange={handleInputChange}
                        className="w-full text-sm px-3.5 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Condición frente al IVA
                      </label>
                      <select
                        name="condicion_iva"
                        value={form.condicion_iva}
                        onChange={handleInputChange}
                        className="w-full text-sm px-3 py-2.5 rounded-xl border border-slate-300 bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                      >
                        {CONDICIONES_IVA.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.nombre}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Ambiente
                      </label>
                      <select
                        name="afip_env"
                        value={form.afip_env}
                        onChange={handleInputChange}
                        className="w-full text-sm px-3 py-2.5 rounded-xl border border-slate-300 bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                      >
                        <option value="production">Producción (Comprobantes Reales)</option>
                        <option value="testing">Homologación / Testing (Pruebas)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Domicilio Fiscal
                      </label>
                      <input
                        type="text"
                        name="domicilio_fiscal"
                        placeholder="Ej: Av. Rivadavia 1234, CABA"
                        value={form.domicilio_fiscal}
                        onChange={handleInputChange}
                        className="w-full text-sm px-3.5 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Número de Ingresos Brutos (IIBB)
                      </label>
                      <input
                        type="text"
                        name="numero_iibb"
                        placeholder="Ej: 27-11098570-9 o Convenio Multilateral"
                        value={form.numero_iibb}
                        onChange={handleInputChange}
                        className="w-full text-sm px-3.5 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Fecha de Inicio de Actividades
                      </label>
                      <input
                        type="text"
                        name="inicio_actividades"
                        placeholder="Ej: 01/11/2012"
                        value={form.inicio_actividades}
                        onChange={handleInputChange}
                        className="w-full text-sm px-3.5 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-3 border-t border-slate-100">
                    <button
                      type="submit"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-sm transition-all shadow-indigo-600/20"
                    >
                      <span>Siguiente: Generar Solicitud</span>
                      <ArrowRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </form>
              )}

              {/* PASO 2: GENERAR SOLICITUD (CSR) */}
              {paso === 2 && (
                <div className="space-y-5">
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                      <KeyIcon className="w-5 h-5 text-indigo-600" />
                      Generación de Claves Criptográficas
                    </h3>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      Al presionar el botón a continuación, el sistema generará una <strong>Clave Privada RSA de 2048 bits</strong> para tu óptica y creará el archivo de <strong>Solicitud de Certificado (CSR)</strong> oficial con tu CUIT ({form.cuit}).
                    </p>
                    <div className="mt-2 text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 p-2.5 rounded-lg flex items-center gap-2">
                      <ShieldCheckIcon className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span>
                        Tu clave privada quedará protegida con <strong>cifrado AES-256-GCM</strong> en el servidor y nunca saldrá de él.
                      </span>
                    </div>
                  </div>

                  <div className="border border-slate-200 rounded-xl p-4 bg-white space-y-2 text-xs text-slate-600">
                    <div className="grid grid-cols-2 gap-2">
                      <div><span className="font-semibold text-slate-700">CUIT:</span> {form.cuit}</div>
                      <div><span className="font-semibold text-slate-700">Punto de Venta:</span> {form.punto_venta}</div>
                      <div><span className="font-semibold text-slate-700">Razón Social:</span> {form.razon_social || 'N/D'}</div>
                      <div><span className="font-semibold text-slate-700">Ambiente:</span> {form.afip_env === 'production' ? 'Producción' : 'Testing'}</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <button
                      type="button"
                      onClick={() => setPaso(1)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-xl hover:bg-slate-100 transition-colors"
                    >
                      <ArrowLeftIcon className="w-4 h-4" />
                      <span>Modificar Datos</span>
                    </button>

                    <button
                      type="button"
                      onClick={handleGenerarCsr}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-md shadow-indigo-600/20 transition-all"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4" />
                      <span>Generar y Descargar Pedido (.CSR)</span>
                    </button>
                  </div>
                </div>
              )}

              {/* PASO 3: GUÍA RÁPIDA EN AFIP */}
              {paso === 3 && (
                <div className="space-y-4">
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                    <InformationCircleIcon className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-900 space-y-1">
                      <p className="font-semibold">¡El archivo de pedido (CSR) ya está en tus Descargas!</p>
                      <p>Seguí estos breves pasos en la web de AFIP con tu Clave Fiscal para obtener tu certificado (.crt):</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {/* Paso 1 AFIP */}
                    <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-indigo-600 text-white font-bold text-xs flex items-center justify-center shrink-0">
                        1
                      </span>
                      <div className="text-xs text-slate-700 space-y-1">
                        <p className="font-bold text-slate-800">Entrá a AFIP</p>
                        <p>
                          Ingresá a <a href="https://www.afip.gob.ar" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline font-semibold">afip.gob.ar</a> con tu CUIT y Clave Fiscal (Nivel 3).
                        </p>
                      </div>
                    </div>

                    {/* Paso 2 AFIP */}
                    <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-indigo-600 text-white font-bold text-xs flex items-center justify-center shrink-0">
                        2
                      </span>
                      <div className="text-xs text-slate-700 space-y-1">
                        <p className="font-bold text-slate-800">Administración de Certificados Digitales</p>
                        <p>
                          Abrí ese servicio y hacé clic en <strong>"Agregar Alias"</strong>. Ingresá un nombre identificador (ej: <code>optica-facturacion</code>) y en el selector de archivo subí el archivo <code>.csr</code> que recién descargaste.
                        </p>
                      </div>
                    </div>

                    {/* Paso 3 AFIP */}
                    <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-indigo-600 text-white font-bold text-xs flex items-center justify-center shrink-0">
                        3
                      </span>
                      <div className="text-xs text-slate-700 space-y-1">
                        <p className="font-bold text-slate-800">Descargá el Certificado (.crt)</p>
                        <p>
                          Al guardar el alias en AFIP, hacé clic en el botón <strong>"Ver"</strong> o <strong>"Descargar"</strong>. Se descargará un archivo con extensión <code>.crt</code>.
                        </p>
                      </div>
                    </div>

                    {/* Paso 4 AFIP */}
                    <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-indigo-600 text-white font-bold text-xs flex items-center justify-center shrink-0">
                        4
                      </span>
                      <div className="text-xs text-slate-700 space-y-1">
                        <p className="font-bold text-slate-800">Vincular Servicio en Administrador de Relaciones</p>
                        <p>
                          Ingresá al servicio <strong>"Administrador de Relaciones de Clave Fiscal"</strong> ➔ Nueva Relación ➔ AFIP / WebServices ➔ <strong>"Facturación Electrónica"</strong> y asignale el alias que creaste en el paso 2.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <button
                      type="button"
                      onClick={() => setPaso(2)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-xl hover:bg-slate-100 transition-colors"
                    >
                      <ArrowLeftIcon className="w-4 h-4" />
                      <span>Volver al CSR</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setPaso(4)}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-md shadow-indigo-600/20 transition-all"
                    >
                      <span>Ya descargué el .crt, continuar</span>
                      <ArrowRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* PASO 4: SUBIR CRT Y PROBAR */}
              {paso === 4 && (
                <div className="space-y-5">
                  <div className="border-2 border-dashed border-slate-300 hover:border-indigo-400 rounded-2xl p-6 text-center bg-slate-50/50 transition-colors">
                    <ArrowUpTrayIcon className="w-10 h-10 text-indigo-500 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-slate-700 mb-1">
                      Cargá el archivo de certificado (.crt) emitido por AFIP
                    </p>
                    <p className="text-xs text-slate-500 mb-4">
                      El sistema verificará matemáticamente que coincida con tu clave privada generada.
                    </p>

                    <input
                      type="file"
                      id="input-crt-file"
                      accept=".crt,.pem"
                      onChange={handleFileChange}
                      className="hidden"
                    />

                    <label
                      htmlFor="input-crt-file"
                      className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-xl hover:bg-indigo-100 cursor-pointer transition-colors"
                    >
                      <DocumentTextIcon className="w-4 h-4" />
                      <span>{archivoCrt ? archivoCrt.name : 'Seleccionar archivo .crt'}</span>
                    </label>

                    {archivoCrt && (
                      <div className="mt-4">
                        <button
                          type="button"
                          onClick={handleSubirCertificado}
                          className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-md shadow-indigo-600/20 transition-all"
                        >
                          <ShieldCheckIcon className="w-4 h-4" />
                          <span>Vincular y Activar Certificado</span>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Estado de activación / Prueba de conexión */}
                  {resultadoVinculacion && (
                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl space-y-3">
                      <div className="flex items-center gap-2 text-emerald-800 font-bold text-sm">
                        <CheckCircleIcon className="w-5 h-5 text-emerald-600" />
                        <span>Certificado Activo y Cifrado</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-emerald-900">
                        <div><span className="font-semibold">CUIT:</span> {resultadoVinculacion.cuit}</div>
                        <div><span className="font-semibold">Punto de Venta:</span> {resultadoVinculacion.punto_venta}</div>
                        <div><span className="font-semibold">Vencimiento:</span> {resultadoVinculacion.valido_hasta || 'N/D'}</div>
                        <div><span className="font-semibold">Ambiente:</span> {resultadoVinculacion.ambiente}</div>
                      </div>

                      <div className="pt-2 border-t border-emerald-200/60 flex items-center justify-between">
                        <button
                          type="button"
                          disabled={testeando}
                          onClick={handleTestConexion}
                          className="inline-flex items-center gap-2 px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition-all disabled:opacity-50"
                        >
                          <ArrowPathIcon className={`w-3.5 h-3.5 ${testeando ? 'animate-spin' : ''}`} />
                          <span>{testeando ? 'Consultando AFIP...' : 'Probar Conexión con AFIP'}</span>
                        </button>

                        {resultadoTest && (
                          <span className={`text-xs font-semibold inline-flex items-center gap-1 ${
                            resultadoTest.ok ? 'text-emerald-700' : 'text-rose-600'
                          }`}>
                            {resultadoTest.ok ? (
                              <>
                                <CheckCircleIcon className="w-4 h-4 text-emerald-600" />
                                <span>Conexión Exitosa (WSAA/WSFE OK)</span>
                              </>
                            ) : (
                              <>
                                <ExclamationTriangleIcon className="w-4 h-4 text-rose-600" />
                                <span>{resultadoTest.mensaje}</span>
                              </>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <button
                      type="button"
                      onClick={() => setPaso(3)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-xl hover:bg-slate-100 transition-colors"
                    >
                      <ArrowLeftIcon className="w-4 h-4" />
                      <span>Volver a las instrucciones</span>
                    </button>

                    <button
                      type="button"
                      onClick={onClose}
                      className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold transition-all"
                    >
                      Listo / Cerrar
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
